import type { ClientSummary } from '../schemas/client';

/**
 * Search over what is on the row.
 *
 * The name and the address, because those are what the operator can see and
 * therefore what they will type — as a substring, since a name is typed a
 * fragment at a time.
 *
 * The tenant id joins them, but only as an EXACT match. It is not on the row,
 * so a substring hit on it would make a client appear for a reason the screen
 * cannot show; a whole id, on the other hand, is something the operator only
 * ever has because they pasted it out of a ticket or a log line, and "no
 * results" is the wrong answer to an id that is right there in the list.
 *
 * ## What used to be here
 *
 * A `ready` / `needs-owner` / `all` filter, defaulting to `ready`. It was
 * removed with its chips: a default that hides rows makes the list answer a
 * narrower question than the one it appears to answer, and the owner state it
 * sorted on is already a column on every row. Searching narrows only when the
 * operator asks it to, and says so by having text in it.
 */
export function searchClients(clients: readonly ClientSummary[], search: string): ClientSummary[] {
    const needle = search.trim().toLowerCase();
    if (needle.length === 0) return [...clients];

    return clients.filter(
        (client) =>
            client.displayName.toLowerCase().includes(needle) ||
            (client.slug ?? '').toLowerCase().includes(needle) ||
            client.tenantId.toLowerCase() === needle,
    );
}
